import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@jornada/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only COLLECTS data and delegates formatting to
 * `buildDiagnosticsReport` (shared, a pure and tested function). If the report were
 * assembled here, every app would have its own version and they would diverge as soon as
 * someone added a field.
 *
 * Receives the SAME array of sections that the screen renders. Previously it received
 * its own object while the screen rendered its rows separately: two
 * sources for the same thing, so adding a row above left the report
 * incomplete without anyone noticing.
 *
 * Labels: uses `Copy`, which is already translated in eleven languages. This
 * catalog DOES NOT have a generic `Copied!` (only "Token copied" and "Backup codes
 * copied", which would be inaccurate here), so confirmation is provided by the icon and the
 * label does not change. Inventing a string would be worse: it would appear in ENGLISH across all
 * eleven languages without i18n gate complaining, because it only validates what is already
 * in the catalog.
 */
/** How long copied indicator remains before returning to normal icon. */
const CONFIRMATION_TIMEOUT_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model that the screen renders. */
  sections: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copy = async () => {
    const text = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.sections(),
    });
    try {
      await navigator.clipboard.writeText(text);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_TIMEOUT_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Ignoring it
      // would leave the user believing they copied: mark the failure and leave the
      // report selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {t(msg`Copy`)}
      </Button>

      {/* Plan B when clipboard is unavailable: display the report
          for manual selection. Without this, the button would be a dead end
          precisely in environments where debugging is needed most. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.sections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
